<template>
  <Modal
    class="tw-translate-y-[-50%] assessment-modal tw-max-w-[1040px]"
    :visible="showRef"
    @close="handleCloseModal(true)"
  >
    <ModalHeader
      class="modal-header"
      :title="title"
      @close="handleCloseModal"
    />
    <div
      class="tw-p-3 tw-border-b-[1px] tw-border-0 tw-border-solid tw-border-[#EFF0F1] md:tw-p-10"
    >
      <div
        class="tw-bg-[#387DFF] tw-inline-flex tw-px-[5px] tw-py-[3px] tw-rounded tw-mr-[10px]"
      >
        <span
          class="tw-text-white tw-text-[11px] tw-font-medium tw-leading-[130%]"
          >{{ props.value.category }}</span
        >
      </div>
      <div
        class="tw-bg-[#F2F8FF] tw-inline-flex tw-px-[5px] tw-py-[3px] tw-rounded"
      >
        <span
          class="tw-text-[#387DFF] tw-text-[11px] tw-font-medium tw-leading-[130%]"
          >{{ props.value.subCategory }}</span
        >
      </div>
      <div class="tw-mt-[12px]">
        <span
          class="tw-mr-[15px] tw-font-semibold tw-text-[20px] tw-text-[#211F58]"
          >{{ props.value.questionNumber }}</span
        >
        <span class="tw-font-normal tw-text-[20px] tw-text-[#211F58]">{{
          props.value.questionTitle
        }}</span>
      </div>
    </div>

    <div
      class="tw-p-[20px] md:tw-p-[40px] !tw-pb-[0px] tw-text-sm tw-font-medium tw-text-[#211F58]"
    >
      <div class="tw-text-lg tw-font-semibold tw-mb-[20px]">
        {{ titleInut }}
      </div>
      <div sm="7" class="tw-font-normal">
        <WysiwygEditor
          v-model="noteData"
          :include-image="false"
          :is-disabled="isChecklistCompleted"
          :height="'200px'"
          :placeholder="
            isChecklistCompleted
              ? ''
              : getComponentUiText(
                  'app-component-assessments-override-question-note-prompt-1',
                )
          "
          @update:value="currentValueSearch"
        />
      </div>
    </div>

    <div class="tw-p-10 modal-assessment">
      <div class="button-container tw-flex tw-justify-end">
        <button
          :class="`hover:tw-bg-[#096cdb] hover:tw-text-[#fff] tw-justify-center tw-h-[39px] tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-self-end tw-gap-2.5 tw-px-[18px] tw-rounded-[26.909px]  tw-bg-[#fff] tw-border-[#387DFF] tw-text-[#387DFF] tw-border-solid tw-border-[1px] tw-mr-[15px] tw-text-center tw-text-[15px] tw-not-italic tw-font-[500] tw-leading-[100%]`"
          @click="closeModal"
        >
          <div>
            {{ getComponentUiText("app-common-action-cancel-button-1") }}
          </div>
        </button>
        <ButtonLoading
          :title="
            getComponentUiText(
              'app-component-assessment-modal-assessment-save-button-1',
            )
          "
          :disabled="isChecklistCompleted"
          :is-loading="isLoading"
          @click-action="addNote"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { getComponentUiText } from "@/utils/utilities";
import { ComputedRef, ref, watch } from "vue";
import Modal from "@/components/common/modal.vue";
import ModalHeader from "@/components/common/modalHeader.vue";
import { IAssessmentQuestion } from "@/interfaces/assessments/IAssessmentQuestion";
import { PropType } from "vue";
import store from "@/store";
import {
  ASSESSMENT_ANSWER_STORE_TYPE,
  checklistStatusId,
} from "@/utils/constants";
import { computed } from "vue";
import ButtonLoading from "@/components/common/buttonLoading.vue";
import WysiwygEditor from "../common/wysiwygEditor.vue";

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
  },
  title: {
    type: String,
    default: "",
  },
  titleInut: {
    type: String,
    default: "",
  },
  inutPlaceholder: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    required: false,
    default: ASSESSMENT_ANSWER_STORE_TYPE.NOTES,
  },
  value: {
    type: Object as PropType<IAssessmentQuestion>,
    default: () => ({}),
    required: true,
  },
});

const isLoading = ref(false);

const isChecklistCompleted: ComputedRef<boolean> = computed(() => {
  const statusId =
    store.getters["Assessment/getChecklistInforDetail"].checklistStatusId;

  return (
    statusId === checklistStatusId.completed ||
    statusId === checklistStatusId.paused ||
    statusId === checklistStatusId.defunct
  );
});

let showRef = ref(props.show);
let notInfoVisible = ref(true);
let noteData = ref(props.value.note);
const isCallApi = ref(false);
watch(
  () => props.show,
  (value) => {
    showRef.value = value;
    if (value) notInfoVisible.value = true;
  },
);
const emit = defineEmits(["update:show"]);
const handleCloseModal = (isOutside = false) => {
  if (isOutside) {
    if (isLoading.value) {
      return;
    }
    addNote();
    return;
  }
  showRef.value = false;
  emit("update:show", showRef.value);
};

const closeModal = () => {
  showRef.value = false;
  emit("update:show", showRef.value);
};

const addNote = async () => {
  if (
    noteData.value === props.value.note ||
    (noteData.value === "" &&
      (props.value.note === undefined || props.value.note === null))
  ) {
    handleCloseModal();
    return;
  }
  isLoading.value = true;

  await store.dispatch("Assessment/updateAnswer", {
    checklistId: props.value.checklistId,
    checklistItemId: props.value.checklistItemId,
    answer: noteData.value,
    type: props.type,
  });

  handleCloseModal();
  isLoading.value = false;
};

const currentValueSearch = (value: any) => {
  noteData.value = value;
};
</script>

<style lang="scss">
.assessment-modal {
  .button-verify.btn.btn-primary[disabled] {
    background-color: #dbdbdb;
    color: #b7b7b7;
    border-color: #dbdbdb;
  }

  .button-verify {
    line-height: 100%;
    background-color: #387dff;
    border-color: #387dff;
  }
}

@media (max-width: 768px) {
  .assessment-modal {
    .modal-assessment,
    .modal-header {
      padding-right: 10px;
      padding-left: 10px;
    }
  }
}
</style>
